import { useCallback } from 'react';
import { Card, IconButton, Link, Typography, useToast } from '@/primer/components';
import { isHttpUrl } from '@/utils';

interface TrackingCardProps {
  trackingCode: string;
  trackingUrl?: string | null;
}

/** Shipment tracking code with a tap-to-copy affordance and a link to the carrier page. */
export function TrackingCard({ trackingCode, trackingUrl }: TrackingCardProps) {
  const { showToast } = useToast();

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(trackingCode);
      showToast({ variant: 'success', message: 'Tracking code copied.' });
    } catch {
      showToast({ variant: 'error', message: 'Could not copy the tracking code.' });
    }
  }, [trackingCode, showToast]);

  return (
    <div className="flex flex-col gap-2">
      <Card>
        <div className="flex flex-row items-center justify-between gap-2 p-4">
          <div className="flex flex-row items-center gap-2">
            <Typography variant="body" size="medium" color="bodylight">
              Tracking code
            </Typography>
            <Typography variant="body" size="medium" color="header" decoration={{ bold: true }}>
              {trackingCode}
            </Typography>
          </div>
          <IconButton icon="copy" aria-label="Copy tracking code" onClick={handleCopy} />
        </div>
      </Card>

      {trackingUrl && isHttpUrl(trackingUrl) && (
        <Typography variant="body" size="small" color="bodylight">
          For detailed info, visit{' '}
          <Link href={trackingUrl} target="_blank" rel="noopener noreferrer" size="small">
            tracking page
          </Link>
        </Typography>
      )}
    </div>
  );
}

export default TrackingCard;
